import AbstractShape, {Dimensions, DimensionProperties} from "./AbstractShape";
import Length from "../../../../source/js/shared/models/Geometry/Length";
import DimensionLine from "@/Models/Lines/DimensionLine";
import OffsetLine from "../Lines/OffsetLine";
import GuideLine from "../Lines/GuideLine";

export class RoundedRectangle extends AbstractShape {
	constructor( dimensions?: object, modifiers?: object ) {
		super( 'rounded-rectangle', dimensions, modifiers );

		this.labelMappings = {
			length: 'width',
			width: 'height',
		};
	}

	getLength(): Length {
		return this.cleanedPrimitives.length;
	}

	getWidth(): Length {
		return this.cleanedPrimitives.width;
	}

	getHeight(): Length {
		return this.cleanedPrimitives.length;
	}

	getRadius(): Length {
		return this.cleanedPrimitives.radius;
	}

	getTopRightRadius(): Length {
		return this.cleanedPrimitives[ 'top_right_radius' ];
	}

	getBottomRightRadius(): Length {
		return this.cleanedPrimitives[ 'bottom_right_radius' ];
	}

	getBottomLeftRadius(): Length {
		return this.cleanedPrimitives[ 'bottom_left_radius' ];
	}

	getTopLeftRadius(): Length {
		return this.cleanedPrimitives[ 'top_left_radius' ];
	}

	getDimensionProperties(): DimensionProperties {
		return { primary: 'width', secondary: 'length', tertiary: 'radius', quaternary: 'top_right_radius' };
	}

	getDimensionLines(ratio = 1, focusedElement = '', forceRender = false): DimensionLine[] {
		let dimensionLines = [];

		if(focusedElement.includes("cutout_measurement") || forceRender) {
			dimensionLines = [
				new DimensionLine( [0, 0], [ 0, this.getWidth().in_mm() ], this.renderDimensionLabel( 'height' ), [-40, 0], ratio, this.getDimensionLineType('measurements-rounded-rectangle-width', focusedElement, forceRender) ), // Vertical line
				new DimensionLine( [0, 0], [ this.getHeight().in_mm(), 0 ], this.renderDimensionLabel( 'width' ), [0, -40], ratio, this.getDimensionLineType('measurements-rounded-rectangle-length', focusedElement, forceRender) ), // Horizontal line
			];
		}

		if( focusedElement.includes("measurements-rounded-rectangle-radius") ) {
			const top = this.getWidth().in_mm() - this.getRadius().in_mm();
			const leftPoint = [0, top];
			const rightPoint = [this.getRadius().in_mm(), top];
			const radiusLine = new DimensionLine(leftPoint, rightPoint, this.renderDimensionLabel('radius'), [0, 0], ratio);
			dimensionLines.push(radiusLine);
		}

		// measurements-rounded-rectangle-top_left_radius
		if( focusedElement.includes("measurements-rounded-rectangle-top_left_radius") ) {
			const top = this.getWidth().in_mm() - this.getTopLeftRadius().in_mm();
			const leftPoint = [0, top];
			const rightPoint = [this.getTopLeftRadius().in_mm(), top];
			const radiusLine = new DimensionLine(leftPoint, rightPoint, this.renderDimensionLabel('top_left_radius'), [0, 0], ratio, this.getDimensionLineType('measurements-rounded-rectangle-top_left_radius', focusedElement, forceRender));
			dimensionLines.push(radiusLine);
		}

		// measurements-rounded-rectangle-top_right_radius
		if( focusedElement.includes("measurements-rounded-rectangle-top_right_radius") ) {
			const top = this.getWidth().in_mm() - this.getTopRightRadius().in_mm();
			const leftPoint = [this.getLength().in_mm() - this.getTopRightRadius().in_mm(), top];
			const rightPoint = [this.getLength().in_mm(), top];
			const radiusLine = new DimensionLine(leftPoint, rightPoint, this.renderDimensionLabel('top_right_radius'), [0, 0], ratio, this.getDimensionLineType('measurements-rounded-rectangle-top_right_radius', focusedElement, forceRender));
			dimensionLines.push(radiusLine);
		}

		// measurements-rounded-rectangle-bottom_left_radius
		if( focusedElement.includes("measurements-rounded-rectangle-bottom_left_radius") ) {
			const top = this.getBottomLeftRadius().in_mm();
			const leftPoint = [0, top];
			const rightPoint = [this.getBottomLeftRadius().in_mm(), top];
			const radiusLine = new DimensionLine(leftPoint, rightPoint, this.renderDimensionLabel('bottom_left_radius'), [0, 0], ratio, this.getDimensionLineType('measurements-rounded-rectangle-bottom_left_radius', focusedElement, forceRender));
			dimensionLines.push(radiusLine);
		}

		// measurements-rounded-rectangle-bottom_right_radius
		if( focusedElement.includes("measurements-rounded-rectangle-bottom_right_radius") ) {
			const top = this.getBottomRightRadius().in_mm();
			const leftPoint = [this.getLength().in_mm() - this.getBottomRightRadius().in_mm(), top];
			const rightPoint = [this.getLength().in_mm(), top];
			const radiusLine = new DimensionLine(leftPoint, rightPoint, this.renderDimensionLabel('bottom_right_radius'), [0, 0], ratio, this.getDimensionLineType('measurements-rounded-rectangle-bottom_right_radius', focusedElement, forceRender));
			dimensionLines.push(radiusLine);
		}

		return dimensionLines;
	}

	getGuideLines(focusedElement = ''): GuideLine[] {
		const guideLines = [];
		if( focusedElement.includes("measurements-rounded-rectangle-radius") ) {
			const left = this.getRadius().in_mm();
			const topPoint = [left, this.getWidth().in_mm()];
			const bottomPoint = [left, this.getWidth().in_mm() - this.getRadius().in_mm()];
			const radiusLine = new GuideLine(topPoint, bottomPoint);
			guideLines.push(radiusLine);
		}

		// measurements-rounded-rectangle-top_left_radius
		if( focusedElement.includes("measurements-rounded-rectangle-top_left_radius") ) {
			const left = this.getTopLeftRadius().in_mm();
			const topPoint = [left, this.getWidth().in_mm()];
			const bottomPoint = [left, this.getWidth().in_mm() - this.getTopLeftRadius().in_mm()];
			const radiusLine = new GuideLine(topPoint, bottomPoint);
			guideLines.push(radiusLine);
		}

		// measurements-rounded-rectangle-top_right_radius
		if( focusedElement.includes("measurements-rounded-rectangle-top_right_radius") ) {
			const left = this.getLength().in_mm() - this.getTopRightRadius().in_mm();
			const topPoint = [left, this.getWidth().in_mm()];
			const bottomPoint = [left, this.getWidth().in_mm() - this.getTopRightRadius().in_mm()];
			const radiusLine = new GuideLine(topPoint, bottomPoint);
			guideLines.push(radiusLine);
		}

		// measurements-rounded-rectangle-bottom_left_radius
		if( focusedElement.includes("measurements-rounded-rectangle-bottom_left_radius") ) {
			const left = this.getBottomLeftRadius().in_mm();
			const topPoint = [left, 0];
			const bottomPoint = [left, this.getBottomLeftRadius().in_mm()];
			const radiusLine = new GuideLine(topPoint, bottomPoint);
			guideLines.push(radiusLine);
		}

		// measurements-rounded-rectangle-bottom_right_radius
		if( focusedElement.includes("measurements-rounded-rectangle-bottom_right_radius") ) {
			const left = this.getLength().in_mm() - this.getBottomRightRadius().in_mm();
			const topPoint = [left, 0];
			const bottomPoint = [left, this.getBottomRightRadius().in_mm()];
			const radiusLine = new GuideLine(topPoint, bottomPoint);
			guideLines.push(radiusLine);
		}

		return guideLines;
	}

	getDefaultDimensions(): Dimensions {
		return {
			width: 50,
			length: 50,
			radius: 2,
			'top_left_radius': 2,
			'top_right_radius': 2,
			'bottom_left_radius': 2,
			'bottom_right_radius': 2,
		};
	}

	getDefaultCutoutDimensions(parentShape: AbstractShape): Dimensions {
		const enclosingRectangle = parentShape.getEnclosingRectangle();
		const parentWidth = enclosingRectangle.width();
		const parentHeight = enclosingRectangle.height();

		const width = parentWidth.divide(2).in_cm();
		return {
			width: parentHeight.divide(2).in_cm(),
			length: parentWidth.divide(2).in_cm(),
			radius: width / 10,
			'top_left_radius': width / 10,
			'top_right_radius': width / 10,
			'bottom_left_radius': width / 10,
			'bottom_right_radius': width / 10,
		};
	}
}
